/**
 * SSML utilities for TTS (Text-to-Speech).
 *
 * ElevenLabs `eleven_flash_v2_5` supports SSML `<break time="Nms" />` tags for
 * controlled pauses inside an utterance. The TTS speaks the surrounding text
 * with the requested silence at the tag position.
 *
 * We use SSML `<break>` in the audio path only; the visible UI must render
 * plain text (no `<break>` tag literals). Callers therefore pass the SSML
 * string to `synthesizeSpeech` and use `stripSsmlTags` to derive the display
 * variant.
 *
 * Related: docs/superpowers/plans/2026-07-06-m2-b-a.md §2.1 (pause length
 * table) and docs/superpowers/specs/2026-07-06-correction-ux-design.md §10.
 */

// Matches <break time="Nms" />, <break time="Nms"/>, and <break  time="Xs" />.
// Only double-quoted values are produced by `buildPauseAck`, so we match those.
const BREAK_TAG_RE = /<break\s+time="[^"]*"\s*\/>/g;

/**
 * Remove SSML `<break>` tags from `text` so it can be rendered as plain UI.
 * Non-tag text is returned unchanged.
 */
export function stripSsmlTags(text: string): string {
  return text.replace(BREAK_TAG_RE, "");
}

/**
 * Build a "confirmation ack" utterance in SSML.
 *
 * Produces `${msg}。<break time="${pauseMs}ms" />${tail}`, i.e. a Japanese
 * full-stop, a break tag with the requested pause, then the confirmation
 * prompt (e.g. "いいですか?").
 *
 * The plain-text form (`stripSsmlTags(result)`) is `${msg}。${tail}` — safe
 * to show in a chat bubble.
 */
export function buildPauseAck(msg: string, pauseMs: number, tail: string): string {
  return `${msg}。<break time="${pauseMs}ms" />${tail}`;
}
